import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown } from "lucide-react";
import { getApiUrl } from "../config/api";
import FreshnessDot from "./FreshnessDot";

// Fallback ticker data if API fails
const TICKER_SYMBOLS = [
  { symbol: "QQQ", name: "NASDAQ 100", isIndex: true },
  { symbol: "SPY", name: "S&P 500", isIndex: true },
  { symbol: "DIA", name: "DOW 30", isIndex: true },
  { symbol: "AAPL", name: "Apple", isIndex: false },
  { symbol: "MSFT", name: "Microsoft", isIndex: false },
  { symbol: "NVDA", name: "NVIDIA", isIndex: false },
  { symbol: "GOOGL", name: "Google", isIndex: false },
  { symbol: "AMZN", name: "Amazon", isIndex: false },
  { symbol: "META", name: "Meta", isIndex: false },
  { symbol: "TSLA", name: "Tesla", isIndex: false },
  { symbol: "AMD", name: "AMD", isIndex: false },
  { symbol: "NFLX", name: "Netflix", isIndex: false },
  { symbol: "JPM", name: "JPMorgan", isIndex: false },
];

const TICKER_TAPE_LS = "sp_ticker_tape_v1";

const TickerTape = ({ onTickerClick }) => {
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Restore last-known prices from localStorage on mount so we never show
  // the "warming up" banner if we have any historical data.
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(TICKER_TAPE_LS) || "null");
      if (cached?.tickers?.length) {
        setTickerData(cached.tickers);
        setLastUpdate(new Date(cached.ts));
        setLoading(false);
      }
    } catch {}
  }, []);

  const fetchTickerData = useCallback(async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/market/ticker-tape`);
      const data = response.data;

      if (data.tickers && data.tickers.length > 0) {
        // Only commit if at least ONE non-zero price came back — otherwise we
        // keep the previously-cached payload so the tape isn't all zeros.
        const hasReal = data.tickers.some((t) => t.price && t.price > 0);
        if (hasReal) {
          setTickerData(data.tickers);
          setLastUpdate(new Date());
          try {
            localStorage.setItem(
              TICKER_TAPE_LS,
              JSON.stringify({ tickers: data.tickers, ts: Date.now() }),
            );
          } catch {}
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching ticker tape data:", error);
      // Keep whatever cached payload is already on screen. Only seed empty
      // symbols if we truly have nothing.
      setTickerData((prev) =>
        prev.length > 0
          ? prev
          : TICKER_SYMBOLS.map((t) => ({
              ...t, price: 0, change: 0, changePercent: 0, isUp: true,
            })),
      );
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchTickerData();
      }
    };
    
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchTickerData]);

  // Check if all prices are zero (data outage — could be rate limit, server cold-start, or network drop)
  const isRateLimited = tickerData.length > 0 && tickerData.every(item => item.price === 0 || !item.price);

  if (loading && tickerData.length === 0) {
    return (
      <div className="bg-slate-900 text-white py-2 overflow-hidden">
        <div className="flex items-center justify-center text-sm text-slate-400">
          Loading market data...
        </div>
      </div>
    );
  }

  if (isRateLimited) {
    return (
      <div className="bg-yellow-900/40 text-yellow-100 py-2 overflow-hidden border-b border-yellow-700" data-testid="ticker-tape-degraded">
        <div className="flex items-center justify-center text-sm">
          <span className="mr-2">⚠️</span>
          Live ticker data is warming up — refreshing automatically.
        </div>
      </div>
    );
  }

  // Duplicate the data for seamless scrolling
  const duplicatedData = [...tickerData, ...tickerData];

  return (
    <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-2 overflow-hidden border-b border-slate-700 relative">
      {/* Freshness indicator */}
      <div className="absolute top-1 right-2 z-10" data-testid="ticker-tape-freshness">
        <FreshnessDot timestamp={lastUpdate} size="xs" showLabel={true} />
      </div>
      <div className="ticker-tape-container">
        <div className="ticker-tape-content animate-ticker">
          {duplicatedData.map((item, index) => (
            <div
              key={`${item.symbol}-${index}`}
              className={`inline-flex items-center gap-2 px-4 cursor-pointer hover:bg-slate-700/50 rounded transition-colors ${
                item.isIndex ? 'border-r border-slate-600 pr-6 mr-2' : ''
              }`}
              onClick={() => onTickerClick && onTickerClick(item.symbol)}
            >
              {/* Symbol and Name */}
              <div className="flex items-center gap-1.5">
                {item.isIndex && (
                  <span className="text-[10px] bg-blue-600 px-1 py-0.5 rounded font-bold">
                    IDX
                  </span>
                )}
                <span className={`font-bold ${item.isIndex ? 'text-yellow-400' : 'text-white'}`}>
                  {item.symbol}
                </span>
                <span className="text-slate-400 text-xs hidden sm:inline">{item.isIndex ? item.name : item.name}</span>
              </div>
              
              {/* Price */}
              <span className="text-white font-medium">
                ${item.price?.toFixed(2) || '0.00'}
              </span>
              
              {/* Change */}
              <div className={`flex items-center gap-0.5 ${item.isUp ? 'text-green-400' : 'text-red-400'}`}>
                {item.isUp ? (
                  <TrendingUp className="w-3 h-3" />
                ) : (
                  <TrendingDown className="w-3 h-3" />
                )}
                <span className="text-xs font-medium">
                  {item.isUp ? '+' : ''}{item.change?.toFixed(2) || '0.00'}
                </span>
                <span className="text-xs">
                  ({item.isUp ? '+' : ''}{item.changePercent?.toFixed(2) || '0.00'}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <style>{`
        .ticker-tape-container {
          width: 100%;
          overflow: hidden;
        }
        
        .ticker-tape-content {
          display: inline-flex;
          white-space: nowrap;
        }
        
        .animate-ticker {
          animation: ticker 60s linear infinite;
        }
        
        .animate-ticker:hover {
          animation-play-state: paused;
        }
        
        @keyframes ticker {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
};

export default TickerTape;
